/**
 * sentencingFilterAxes — the finder's filter axes for the inventory's chip rail
 * (components/inventory/FilterRail, FilterChip, AddFilterPicker), the same registry shape
 * as contexts/inventoryFilterAxes.js: one entry per axis, read by the rail, the chips and
 * the picker. Values come from the server's facets (each facet already leaves out its own
 * filter, so the picker's counts are contextual for free).
 *
 * State is chips: [{axis, value}] — AND across axes, OR within an axis — and the four boxes
 * (name, age, term, the facts search) beside the rail. chipsToFilters / filtersToChips go
 * to and from the API's filter names (the ?f= link format of browseLink).
 */
import { AFTER_APPEAL_LABELS, KNOWN_FROM_LABELS, MODIFIER_LABELS, OPINION_KIND_LABELS } from './sentencing';

const GROUP = {
  what: 'What was sentenced',
  who: 'Who',
  where: 'Where and when',
  circumstances: 'Circumstances',
};

function facetAxis(key, label, icon, group, facet, filter, more = {}) {
  return { key, label, icon, group, facet, filter, ...more };
}

// A yes/no circumstance: one chip, one value.
function boolAxis(key, label, icon, group, filter, more = {}) {
  return { key, label, icon, group, filter, bool: true, boolLabel: 'yes', facet: filter, ...more };
}

export const SENTENCING_AXES = {
  mca: facetAxis('mca', 'Statute', '§', GROUP.what, 'mca_sections', 'mca_codes'),
  mca2: facetAxis('mca2', '2nd charge', '§', GROUP.what, 'mca_count2', 'mca_codes_count2', {
    // Co-occurrence values exist only once a primary statute is picked.
    visibleWhen: (ctx) => (ctx.chips || []).some((c) => c.axis === 'mca'),
  }),
  stype: facetAxis('stype', 'Sentence type', '📋', GROUP.what, 'procedural_sentence_types', 'procedural_sentence_types'),
  commit: facetAxis('commit', 'Commitment', '🏢', GROUP.what, 'commit_types', 'commit_types'),
  susp: facetAxis('susp', 'Suspension', '⏸', GROUP.what, 'suspensions', 'suspensions'),
  sex: facetAxis('sex', 'Sex', '👤', GROUP.who, 'sexes', 'sexes'),
  race: facetAxis('race', 'Race', '👤', GROUP.who, 'races', 'races'),
  county: facetAxis('county', 'County', '📍', GROUP.where, 'counties', 'counties'),
  judge: facetAxis('judge', 'Judge', '⚖️', GROUP.where, 'judges', 'judges'),
  year: facetAxis('year', 'Year', '📅', GROUP.where, 'sentencing_years', 'sentencing_years', { numeric: true }),
  weapon: boolAxis('weapon', MODIFIER_LABELS.weapon, '⚠️', GROUP.circumstances, 'modifiers_weapon'),
  pfo: boolAxis('pfo', MODIFIER_LABELS.pfo, '↻', GROUP.circumstances, 'modifiers_pfo'),
  attempt: boolAxis('attempt', MODIFIER_LABELS.attempt, '◐', GROUP.circumstances, 'modifiers_attempt'),
  conspiracy: boolAxis('conspiracy', MODIFIER_LABELS.conspiracy, '👥', GROUP.circumstances, 'modifiers_conspiracy'),
  fate: facetAxis('fate', 'On appeal', '🏛️', GROUP.circumstances, 'after_appeal', 'after_appeal', { valueLabels: AFTER_APPEAL_LABELS, opinions: true }),
  kind: facetAxis('kind', 'Document', '📄', GROUP.circumstances, 'opinion_kinds', 'opinion_kinds', { valueLabels: OPINION_KIND_LABELS, opinions: true }),
  known: facetAxis('known', 'Known from', '🔗', GROUP.circumstances, 'known_from', 'known_from', { valueLabels: KNOWN_FROM_LABELS, opinions: true }),
  reversed: boolAxis('reversed', 'Reversed convictions', '⛔', GROUP.circumstances, 'exclude_conviction_reversed', { boolLabel: 'left out', opinions: true }),
};

export const SENTENCING_AXIS_ORDER = [
  'mca', 'mca2', 'stype', 'commit', 'susp',
  'sex', 'race',
  'county', 'judge', 'year',
  'weapon', 'pfo', 'attempt', 'conspiracy', 'fate', 'kind', 'known', 'reversed',
];

for (const axis of Object.values(SENTENCING_AXES)) {
  axis.formatValue = (value) => {
    if (axis.bool) return axis.boolLabel;
    if (axis.valueLabels && axis.valueLabels[value]) return axis.valueLabels[value];
    return String(value);
  };
}

export const ADD_FILTER_HINT = ' — statute, county, judge, year, sentence type, commitment, sex, race, weapon, on appeal, …';

function axisVisible(axis, ctx) {
  if (axis.opinions && !ctx.opinionsLoaded) return false;
  return !axis.visibleWhen || axis.visibleWhen(ctx);
}

/**
 * The picker's rows, in the order of the groups: one row per facet value, one per yes/no
 * circumstance. `ctx` = { facets, opinionsLoaded, chips }; the row list the picker passes
 * first is unused (the values come from the server's facets).
 */
export function getSentencingPickerRows(_rows, _activeFilters, ctx = {}) {
  const facets = ctx.facets || {};
  const out = [];
  for (const key of SENTENCING_AXIS_ORDER) {
    const axis = SENTENCING_AXES[key];
    if (!axisVisible(axis, ctx)) continue;
    if (axis.bool) {
      const yes = (facets[axis.facet] || []).find((o) => o.value === true || o.value === 'true');
      out.push({ axis: key, axisLabel: axis.label, axisIcon: axis.icon, value: 'yes', label: axis.boolLabel, count: yes ? yes.count : undefined });
      continue;
    }
    for (const o of facets[axis.facet] || []) {
      const value = axis.numeric ? Number(o.value) : o.value;
      const label = axis.valueLabels?.[o.value] || (o.label ? `${o.value} — ${o.label}` : String(o.value));
      out.push({ axis: key, axisLabel: axis.label, axisIcon: axis.icon, value, label, count: o.count });
    }
  }
  return out;
}

/** chips → the API's filter names (lists per axis; a yes/no circumstance as the API reads it). */
export function chipsToFilters(chips) {
  const f = {};
  for (const c of chips || []) {
    const axis = SENTENCING_AXES[c.axis];
    if (!axis) continue;
    if (axis.bool) {
      f[axis.filter] = axis.key === 'reversed' ? true : [true];
      continue;
    }
    (f[axis.filter] = f[axis.filter] || []).push(axis.numeric ? Number(c.value) : c.value);
  }
  return f;
}

/** The API's filter names (a ?f= link) → chips; the boxes' values are left to the caller. */
export function filtersToChips(filters) {
  const chips = [];
  const f = filters || {};
  for (const key of SENTENCING_AXIS_ORDER) {
    const axis = SENTENCING_AXES[key];
    const v = f[axis.filter];
    if (axis.bool) {
      if (v === true || (Array.isArray(v) && v.includes(true))) chips.push({ axis: key, value: 'yes' });
      continue;
    }
    if (Array.isArray(v)) for (const value of v) chips.push({ axis: key, value: axis.numeric ? Number(value) : value });
  }
  // a link written before the finder carried `source`
  if (f.source === 'appellate' && !chips.some((c) => c.axis === 'known')) chips.push({ axis: 'known', value: 'opinion' });
  return chips;
}

// The filters Browse's boxes hold (the name, the age, the term, the years, the facts search).
const BOX_KEYS = new Set([
  'last_names', 'age_at_sentencing_min', 'age_at_sentencing_max', 'term_months_min', 'term_months_max',
  'sentencing_year_min', 'sentencing_year_max', 'facts_query', 'facts_scope',
]);
const NOT_FILTERS = new Set(['source', 'sort_by', 'sort_dir', 'limit', 'offset', 'include_details']);

const sameValue = (a, b) => {
  if (Array.isArray(a) && Array.isArray(b)) {
    const key = (x) => [...x].map(String).sort().join('\u0000');
    return key(a) === key(b);
  }
  return a === b || String(a) === String(b);
};

/**
 * A link's filters (the API's names, as a chat card wrote them) → what Browse holds: the
 * chips, the boxes, the sort, and `extra` — every filter the rail and the boxes cannot show
 * (a total-term range, an offense label, a docket, a first name, a modifier set to "no"),
 * kept verbatim and sent with every query, so "Open in Browse" opens the SAME set the card
 * was computed over. A filter the chips carry exactly is not repeated in `extra`.
 */
export function browseStateFromFilters(filters, sortKeys = new Set()) {
  const f = filters && typeof filters === 'object' ? filters : {};
  const chips = filtersToChips(f);
  const carried = chipsToFilters(chips);
  const extra = {};
  for (const [key, value] of Object.entries(f)) {
    if (NOT_FILTERS.has(key) || value == null || value === '' || value === false || (Array.isArray(value) && value.length === 0)) continue;
    if (BOX_KEYS.has(key)) continue;
    if (key in carried && sameValue(carried[key], value)) continue;
    extra[key] = value;
  }
  const num = (v) => (Number.isFinite(Number(v)) && v !== '' && v != null ? String(v) : '');
  const lastNames = Array.isArray(f.last_names) ? f.last_names.filter(Boolean).map(String) : [];
  // the name box holds one last name; several stay as a filter of their own
  if (lastNames.length > 1) extra.last_names = lastNames;
  if (f.facts_scope === 'opinion' && !(typeof f.facts_query === 'string' && f.facts_query.trim())) delete extra.facts_scope;
  return {
    chips,
    extra,
    name: lastNames.length === 1 ? lastNames[0] : '',
    age: { min: num(f.age_at_sentencing_min), max: num(f.age_at_sentencing_max) },
    term: { min: num(f.term_months_min), max: num(f.term_months_max) },
    years: { min: num(f.sentencing_year_min), max: num(f.sentencing_year_max) },
    facts: { query: typeof f.facts_query === 'string' ? f.facts_query : '', scope: f.facts_scope === 'opinion' ? 'opinion' : 'facts' },
    sort: sortKeys.has(f.sort_by) ? { by: f.sort_by, dir: f.sort_dir === 'desc' ? 'desc' : 'asc' } : null,
  };
}
